/**
 * Global Socket Event Router
 * 
 * Registers global socket.io event listeners that route events to Zustand stores.
 * Eliminates race conditions by ensuring listeners are active from connectSocket()
 * to disconnectSocket(), independent of React component mount/unmount lifecycle.
 */

import { Socket } from 'socket.io-client'
import { createElement, type ReactNode } from 'react'
import { flushSync } from 'react-dom'
import { EmojiText } from '@/components/low/AnimatedEmoji'
import { useGameStore, PaywallData, parseTVSidebarWidth } from '@/stores/gameStore'
import type { TVSidebarWidth } from '@/stores/gameStore'
import { useAudioStore, WhisperWord } from '@/stores/audioStore'
import { usePlayerStore } from '@/stores/playerStore'
import { audioService } from '@/services/audio'
import { getTtsProgress, clearTtsProgress } from '@/services/ttsProgress'
import type { QuestionPanelDefinition } from '@/types/actions'
import { toast } from 'sonner'

// billing:paywall_required payload structure
interface PaywallRequiredEvent {
  version: 1
  timestamp: string
  data: PaywallData
}

// session:closed payload structure (see docs/sdk-event-contract.md)
interface SessionClosedEvent {
  session_id: string
  reason: 'last_player_left' | 'host_closed' | 'abandoned'
}

// Timer tracking for auto-hide cleanup
const autoHideTimers = new Map<string, ReturnType<typeof setTimeout>>()

function clearAutoHideTimer(key: string) {
  const existing = autoHideTimers.get(key)
  if (existing) {
    clearTimeout(existing)
    autoHideTimers.delete(key)
  }
}

// ============================================================
// VIBRATION HELPER
// ============================================================

const HAPTIC_PATTERNS: Record<string, number[]> = {
  question: [100, 50, 100, 50, 200],
  success: [50],
  error: [200, 100, 200],
  alert: [100, 50, 100, 50, 100, 50, 100],
}

function triggerVibration(pattern: string | number[]) {
  if (!navigator.vibrate) return
  const resolved = typeof pattern === 'string'
    ? HAPTIC_PATTERNS[pattern] || [100]
    : pattern
  navigator.vibrate(resolved)
}

// ============================================================
// PLAYER FILTERING HELPER
// ============================================================

function isTargetedAtMe(targetPlayers?: string[]): boolean {
  if (!targetPlayers || targetPlayers.length === 0) return true
  const myId = usePlayerStore.getState().playerId
  return !!myId && targetPlayers.includes(myId)
}

// ============================================================
// GLOBAL EVENT HANDLERS
// ============================================================

function handlePhoneInfoUpdated(payload: {
  content_html: string
  duration_ms?: number
  unsafe_html?: boolean
  target_players?: string[]
}) {
  if (!isTargetedAtMe(payload.target_players)) return

  useGameStore.getState().updatePhoneInfo(
    payload.content_html,
    payload.unsafe_html ?? false
  )

  // Auto-hide timer
  if (payload.duration_ms) {
    clearAutoHideTimer('phoneInfo')
    const timer = setTimeout(() => {
      useGameStore.getState().updatePhoneInfo('', false)
      autoHideTimers.delete('phoneInfo')
    }, payload.duration_ms)
    autoHideTimers.set('phoneInfo', timer)
  }
}

function handleTVRegionUpdated(payload: {
  region: string
  content_html: string
  duration_ms?: number
  unsafe_html?: boolean
  sidebar_width?: TVSidebarWidth
}) {
  useGameStore.getState().updateTVRegion(
    payload.region as 'header' | 'sidebar' | 'main' | 'footer' | 'fullscreen',
    payload.content_html,
    payload.unsafe_html ?? false
  )

  // Backend always sends sidebar_width together with sidebar content
  if (payload.region === 'sidebar') {
    useGameStore.getState().setTVSidebarWidth(parseTVSidebarWidth(payload.sidebar_width))
  }

  // Auto-hide for fullscreen only
  if (payload.region === 'fullscreen' && payload.duration_ms) {
    clearAutoHideTimer('tvFullscreen')
    const timer = setTimeout(() => {
      useGameStore.getState().updateTVRegion('fullscreen', '', false)
      autoHideTimers.delete('tvFullscreen')
    }, payload.duration_ms)
    autoHideTimers.set('tvFullscreen', timer)
  }
}

function handleQuestionAsk(payload: {
  request_id: string
  panel: QuestionPanelDefinition
  target_players?: string[]
}) {
  if (!isTargetedAtMe(payload.target_players)) return
  const current = useGameStore.getState().currentQuestion
  if (current) {
    console.warn(`Overwriting active question ${current.requestId} with ${payload.request_id}`)
  }
  useGameStore.getState().setCurrentQuestion(payload.request_id, payload.panel)
  triggerVibration('question')
}

function handleQuestionProgress(payload: {
  request_id: string
}) {
  // Telemetry/progress only — does NOT modify currentQuestion.
  // Panel lifecycle is driven exclusively by game:question:closed.
  console.log(`[question:progress] ${payload.request_id}`)
}

function handleQuestionClosed(payload: {
  request_id: string
  reason: string
  target_players?: string[]
}) {
  if (!isTargetedAtMe(payload.target_players)) return
  const current = useGameStore.getState().currentQuestion
  if (current && current.requestId === payload.request_id) {
    useGameStore.getState().clearCurrentQuestion()
    triggerVibration(payload.reason === 'answered' ? 'success' : 'error')
  }
}

function handleHapticTrigger(payload: {
  pattern: string | number[]
  target_players?: string[]
}) {
  if (!isTargetedAtMe(payload.target_players)) return
  triggerVibration(payload.pattern)
}

function handleLoadingUpdated(payload: {
  visible: boolean
  messages: string[]
}) {
  if (payload.visible) {
    useGameStore.getState().setLoadingOverlay(payload)
  } else {
    useGameStore.getState().clearLoadingOverlay()
  }
}

function handleTtsWhisperUpdate(payload: {
  stream_id: string
  words: WhisperWord[]
  complete: boolean
}) {
  const store = useAudioStore.getState()
  
  // Ignore updates for old streams (out-of-order or reconnect)
  if (payload.stream_id !== store.streamId) {
    console.log('[TTS:WHISPER] Ignoring update for old stream:', payload.stream_id)
    return
  }
  
  store.setWhisperData(payload.words, payload.complete)
}

export interface TtsPlayPayload {
  stream_id: string
  url: string
  whisper_url?: string
  cache_key?: string
}

/** Single entry point for starting TTS — used by `tts:play` and REST `active_tts`. */
export function handleTtsPlay(payload: TtsPlayPayload) {
  const store = useAudioStore.getState()
  const cacheKey = payload.cache_key || payload.stream_id
  
  // Log for E2E test capture (full payload for debugging)
  console.log('[TTS:PLAY]', JSON.stringify(payload))

  if (payload.stream_id === store.streamId) {
    console.log('[TTS:PLAY] Duplicate stream ignored:', payload.stream_id)
    return
  }
  if (payload.stream_id === store.lastFinishedStreamId) {
    console.log('[TTS:PLAY] Finished stream ignored:', payload.stream_id)
    return
  }
  
  const savedProgress = getTtsProgress(cacheKey)

  if (savedProgress !== null) {
    // Resume after refresh — skip audio, show all captions
    flushSync(() => {
      store.setLoading(payload.stream_id, payload.url, cacheKey, payload.whisper_url)
    })
    store.setResumed(payload.stream_id, cacheKey)
    clearTtsProgress(cacheKey)
  } else {
    // Force synchronous render of loading state before useEffect triggers playback
    flushSync(() => {
      store.setLoading(payload.stream_id, payload.url, cacheKey, payload.whisper_url || null)
    })
  }
}

function handleTtsStop() {
  const store = useAudioStore.getState()
  if (store.cacheKey) clearTtsProgress(store.cacheKey)
  store.setStopped()
  audioService.stop()
}

function handleGameFinished(payload: {
  message?: string | null
  return_to_lobby_in_ms?: number
}) {
  const delay = payload.return_to_lobby_in_ms || 5000

  if (payload.message) {
    toast.success(payload.message, {
      duration: delay,
      icon: '🎉',
    })
  }

  // Store pending redirect — React component will execute navigate()
  useGameStore.getState().setPendingRedirect(delay, 'lobby')
}

export function handleGameEnded() {
  const store = useGameStore.getState()
  // Cancel any pending redirect from game:finished (race condition handling)
  store.clearPendingRedirect()
  // Reset all game-specific state (tvRegions, phoneInfo, etc.), set phase=lobby
  store.resetGameState()
  // Trigger immediate redirect (delay=0)
  store.setPendingRedirect(0, 'lobby')
}

/**
 * Session moved to CLOSED — the session no longer exists for the client.
 * Only schedules the redirect; store clearing happens at navigation time
 * in useRedirectWatcher (clearing here would wipe the pending redirect itself).
 * The session id is recorded so a redirect that was never consumed cannot fire
 * inside a different session joined later.
 */
export function handleSessionClosed(payload: SessionClosedEvent) {
  let sessionId: string | null
  if (typeof payload?.session_id === 'string') {
    sessionId = payload.session_id
  } else {
    // Contract violation — fall back to the session this socket belongs to, so the
    // redirect stays scoped instead of leaking into a session joined later.
    console.error('[socketEventRouter] session:closed without session_id:', payload)
    sessionId = usePlayerStore.getState().sessionUuid
  }
  useGameStore.getState().setPendingRedirect(0, 'home', sessionId)
}

function handleBillingPaywallRequired(payload: PaywallRequiredEvent) {
  useGameStore.getState().setPaywallData(payload.data)
}

/** Toast icons live outside any container the emoji hook walks, so they animate explicitly. */
function animatedIcon(character: string): ReactNode {
  return createElement(EmojiText, null, character)
}

function handleGameNotification(payload: {
  type: 'error' | 'success' | 'warning' | 'info'
  message: string
  duration_ms?: number
}) {
  const duration = payload.duration_ms === undefined ? 3000 : payload.duration_ms
  switch (payload.type) {
    case 'error': toast.error(payload.message, { duration }); break
    case 'success': toast.success(payload.message, { duration }); break
    case 'warning': toast(payload.message, { duration, icon: animatedIcon('⚠️') }); break
    case 'info': default: toast(payload.message, { duration, icon: animatedIcon('💡') }); break
  }
}

// ============================================================
// REGISTRATION / CLEANUP
// ============================================================

let cleanupFn: (() => void) | null = null

export function registerGlobalListeners(socket: Socket): void {
  // Prevent double registration
  if (cleanupFn) {
    cleanupFn()
  }

  socket.on('game:phone:info_updated', handlePhoneInfoUpdated)
  socket.on('game:tv:region_updated', handleTVRegionUpdated)
  socket.on('game:question:ask', handleQuestionAsk)
  socket.on('game:question:progress', handleQuestionProgress)
  socket.on('game:question:closed', handleQuestionClosed)
  socket.on('haptic:trigger', handleHapticTrigger)
  socket.on('game:loading:updated', handleLoadingUpdated)
  socket.on('tts:play', handleTtsPlay)
  socket.on('tts:whisper:update', handleTtsWhisperUpdate)
  socket.on('tts:stop', handleTtsStop)
  socket.on('game:finished', handleGameFinished)
  socket.on('game:ended', handleGameEnded)
  socket.on('game:notification', handleGameNotification)
  socket.on('billing:paywall_required', handleBillingPaywallRequired)
  socket.on('session:closed', handleSessionClosed)

  cleanupFn = () => {
    socket.off('game:phone:info_updated', handlePhoneInfoUpdated)
    socket.off('game:tv:region_updated', handleTVRegionUpdated)
    socket.off('game:question:ask', handleQuestionAsk)
    socket.off('game:question:progress', handleQuestionProgress)
    socket.off('game:question:closed', handleQuestionClosed)
    socket.off('haptic:trigger', handleHapticTrigger)
    socket.off('game:loading:updated', handleLoadingUpdated)
    socket.off('tts:play', handleTtsPlay)
    socket.off('tts:whisper:update', handleTtsWhisperUpdate)
    socket.off('tts:stop', handleTtsStop)
    socket.off('game:finished', handleGameFinished)
    socket.off('game:ended', handleGameEnded)
    socket.off('game:notification', handleGameNotification)
    socket.off('billing:paywall_required', handleBillingPaywallRequired)
    socket.off('session:closed', handleSessionClosed)

    // Clear all auto-hide timers
    for (const timer of autoHideTimers.values()) {
      clearTimeout(timer)
    }
    autoHideTimers.clear()
    cleanupFn = null
  }
}

export function unregisterGlobalListeners(): void {
  if (cleanupFn) {
    cleanupFn()
  }
}
